export const en = {
  nav: {
    story: "Story",
    gallery: "Gallery",
    location: "Location",
    contact: "Contact",
    home: "Home",
    reserve: "Reserve",
  },
  hero: {
    tag: "Kioni · Ithaca · Ionian Sea",
    headline_1: "A quiet garden",
    headline_2: "above the harbour.",
    sub: "A tranquil summer residence for guests who trade the fast lane for the shade of an olive tree and the sound of cicadas at dusk.",
    cta_book: "Reserve the season",
    cta_learn: "Discover the villa",
  },
  story: {
    overline: "01 · The Villa",
    heading: "A place to slow down.",
    body_1: "Set on a terraced hillside above the fishing village of Kioni, Ithaca Garden Villa is a private retreat built for stillness. Days unfold in the garden — under the fig and olive trees, along the shaded patio, in a quiet courtyard where the only intrusion is the sound of cicadas.",
    body_2: "Inside, the villa is finished with a light hand: linen upholstery, natural stone, muted plaster, and considered fixtures throughout. A single, private residence — never shared — for the length of your stay.",
    bedrooms: "Bedrooms",
    guests: "Guests",
    min_stay: "Minimum stay",
    nights: "nights",
    bedding: "One queen bed + two single beds — sleeps four in quiet comfort. Additional beds and pillows can be arranged on request for a small fee.",
  },
  amenities: {
    overline: "02 · Amenities",
    heading: "The essentials, considered.",
    items: [
      ["Shaded patio", "Long lunches out of the midday sun"],
      ["Outdoor garden", "Fig, olive & citrus, in terraces"],
      ["Outdoor shower", "For rinsing the sea from your skin"],
      ["Air-conditioning", "Silent, whole-house climate"],
      ["Wi-Fi", "Fibre uplink throughout the villa"],
      ["Workstation", "Ergonomic desk & Herman Miller chair"],
      ["Apple TV", "Streaming on every screen"],
      ["Nespresso", "Fresh espresso, from first light"],
      ["Washing machine", "In-villa, discreetly placed"],
      ["Double parking", "Secure, off-street, for two cars"],
    ],
  },
  villa_reel: {
    overline: "The Villa",
    heading: "Every room, every corner.",
    body: "A slow walk through the whole house — from the stone terraces and olive-shaded patios to the private bedrooms and quiet reading nooks. Filmed and edited in-house so you know exactly what you're booking.",
    caption: "A minute inside Ithaca Garden Villa",
  },
  films: {
    overline: "In Motion",
    heading: "The island, from a slower angle.",
    body: "Films of Kioni harbour, the winding coastline, and the villa itself — moving portraits to sit with before your stay begins.",
  },
  urgency: {
    overline: "Availability",
    night_singular: "night",
    night_plural: "nights",
    remaining_in: "still open in",
    best_window: "Longest open window",
    from: "from",
    cta: "Reserve these dates",
  },
  location: {
    overline: "03 · Location",
    heading: "A two-minute walk to the sea.",
    body: "The villa is a short stroll from the crescent harbour of Kioni, minutes on foot to the village tavernas, and only two minutes from the island's quietest beaches. Convenience and privacy at this level of luxury cannot be matched anywhere in Kioni.",
    port: "Kioni harbour",
    airport: "Nearest airport",
    port_val: "5 min walk",
    airport_val: "Kefalonia · 2 h",
    find_us_overline: "Find us",
    address_line: "Kioni · Ithaca 28301 · Greece",
    coords: "38.4479° N, 20.6895° E",
    howto: {
      overline: "Getting here",
      heading: "The journey to Ithaca.",
      intro: "The reward of Ithaca is that it is not easy to reach — quiet, unhurried, deliberate. Three routes we most often recommend.",
      routes: [
        {
          badge: "01",
          title: "By air + ferry",
          duration: "From Athens · half a day",
          steps: [
            "Fly Athens → Kefalonia (Argostoli) — around 40 minutes.",
            "Argostoli → Sami port by taxi or hire car (about 45 min).",
            "Ferry Sami → Piso Aetos (about 30 min) via Ionian Pelagos.",
            "Carriers on Athens–Kefalonia: Aegean, Olympic Air, EasyJet, Ryanair. Flight and ferry times don't always align — a private sea taxi is the elegant workaround.",
          ],
        },
        {
          badge: "02",
          title: "By road + ferry",
          duration: "From Athens · full day",
          steps: [
            "Drive Athens → Kyllini (~3 h 25, 287 km), then ferry via Poros to Ithaca.",
            "Or Athens → Astakos (~3 h 52, 299 km), then ferry Astakos → Vathi.",
            "KTEL buses run daily from Athens Kifisou terminal, with connecting ferry tickets bookable at the same window.",
          ],
        },
        {
          badge: "03",
          title: "By private sea taxi",
          duration: "From Kefalonia · ~30 min",
          steps: [
            "For guests arriving late or off-schedule, a private sea taxi from Kefalonia is the fastest, most flexible option.",
            "Operators: Odysseas Sea Taxi, Ithaca Transfers (Kavallieratos), Zaharatos Sea Transfers.",
            "We arrange this on your behalf on request — write to us when your flights are confirmed.",
          ],
        },
      ],
      ferries_heading: "Ferry operators",
      ferries: [
        ["Levante Ferries", "Patras · Sami · Ithaca and Kyllini · Poros · Ithaca"],
        ["Ionian Pelagos", "Astakos · Kefalonia · Ithaca"],
        ["Lefkada Palace", "Passenger service across the Ionian Islands"],
      ],
      numbers_heading: "Useful numbers",
      numbers: [
        ["Ithaca ferry booking", "+30 26740 32104"],
        ["Kefalonia ferry booking", "+30 26740 23405"],
        ["Astakos ferry booking", "+30 26460 38020"],
        ["Patras Port Authority", "+30 2610 341002"],
        ["Kyllini Port Authority", "+30 26230 92211"],
      ],
      note: "Ferry schedules are weather-dependent — always confirm the day before travel. We are happy to coordinate transfers from any Ionian arrival point.",
      source: "Travel information adapted from ithacagreece.com",
    },
  },
  testimonial: {
    tag: "Guest, Summer 2025",
    quote: "\"We came for a week and stayed for two. It is the most quiet place I have ever known, and the light — the light is impossible to describe.\"",
    author: "— A. K. · London",
  },
  concierge: {
    overline: "Concierge, on request",
    heading: "Everything else, quietly arranged.",
    body: "The villa comes with the calm attention of Stella, our on-island housekeeper and concierge. From a candlelit private dinner on the terrace to a full-day boat charter, she arranges the small luxuries that make an Ithacan week feel effortless. Write to us at booking, or reply to your welcome email — Stella takes it from there.",
    contact_line: "Requests are best made a few days ahead so we can bring in the right chef, boat, or therapist for your dates.",
    instagram_line: "Follow the season on Instagram",
    instagram_recent: "Recent from Kioni",
    services: [
      ["Private chef", "Terrace dinners, seafood platters, or a full tasting menu — sourced from the Kioni fishing boats and the island's own producers."],
      ["Boat charters", "Half or full-day sailing to Fiskardo, Meganissi and the hidden coves of Ithaca. Skippered classic caïques or modern day-cruisers."],
      ["In-villa massage & yoga", "A calming Swedish massage, deep-tissue, or a private sunrise yoga session on the garden patio."],
      ["Grocery pre-stocking", "The fridge and pantry fully stocked to your preferences before you arrive — pastries, coffee, wine, natural cosmetics, anything you need."],
      ["Transfers & sea taxi", "Meet-and-greet at Kefalonia airport, private car transfers, and priority sea-taxi bookings when ferries don't align."],
      ["Excursions & guided walks", "Sunset olive-grove walks, wine tastings in Kefalonia, a private historian's tour of Homer's Ithaca."],
    ],
  },
  cta: {
    overline: "04 · Reserve",
    heading: "Book your summer in the garden.",
    body: "Bookings are on a first-come basis. Payment secured via Stripe. The season runs May through October, with a minimum stay of {n} nights.",
    button: "Check availability",
    from: "From",
    per_night: "per night",
    cleaning: "Cleaning fee €{n}",
    min: "Minimum {n} nights",
    deposit: "Refundable deposit €{n} · returned 3 days after check-out",
  },
  newsletter: {
    overline: "Quiet season, private list",
    heading: "The letter from the garden.",
    body: "Once or twice a year, a short letter from Ithaca — early booking windows for next summer, the odd quiet-season week, notes on the harvest and the sea.",
    email_placeholder: "your email",
    name_placeholder: "your name (optional)",
    submit: "Subscribe",
    success: "Thank you — you're on the list.",
    error: "Please enter a valid email.",
    consent: "By subscribing you agree to receive occasional emails from the owner. No lists shared, unsubscribe any time.",
  },
  waitlist: {
    overline: "These dates are taken",
    heading: "Join the waitlist",
    body: "If a guest cancels or their reservation expires, we'll email you the moment your week opens up. First to reply, first to book.",
    name_label: "Name",
    email_label: "Email",
    phone_label: "Phone",
    notes_label: "Notes",
    name_placeholder: "your name",
    email_placeholder: "your email",
    phone_placeholder: "your phone (optional)",
    notes_placeholder: "anything we should know (optional)",
    flex_label: "How flexible are your dates?",
    flex_none: "Exact dates only",
    flex_3: "± 3 days",
    flex_7: "± 1 week",
    submit: "Notify me if these open up",
    success: "You're on the list — we'll write the moment your week is free.",
    already: "You are already on the waitlist for those dates.",
    error: "Something went wrong. Please try again shortly.",
  },
  review: {
    overline: "A note from the garden",
    heading: "How was Ithaca?",
    body: "A quiet, private review — a rating, a line you'd share with future guests only if you agree, and any thoughts on what the villa should have next.",
    invalid: "This review link is no longer valid.",
    already: "You've already left a note — thank you.",
    rating_label: "How would you rate your stay?",
    display_label: "Publish under which name?",
    display_placeholder: "First name, or 'Anonymous'",
    quote_label: "A line we could share with future guests (optional)",
    quote_placeholder: "The mornings were slower than any I've had in years.",
    quote_consent: "You may publish this quote (as an anonymous or first-name testimonial) on the website.",
    private_label: "A private note to the owner (optional — never published)",
    private_placeholder: "Anything you'd like us to know.",
    amenities_label: "What should the villa have next?",
    amenities_body: "Tick anything you'd have liked to find here.",
    amenities_bedroom: "An additional bedroom",
    amenities_parking: "Garage parking",
    amenities_pool: "A pool",
    amenities_jacuzzi: "A jacuzzi",
    amenities_gym: "A small gym",
    amenities_chef: "A private chef arranged in advance",
    amenities_breakfast: "Breakfast service on the terrace",
    amenities_other_label: "Anything else",
    amenities_other_placeholder: "Kayak rental, sunset boat, driver on call…",
    submit: "Send private note",
    success: "Thank you. Your note is with us.",
    error: "Something went wrong. Please try again.",
  },
  contact: {
    overline: "05 · Enquire",
    heading: "Speak with the owner directly.",
    body: "For custom stays, private events, or questions about the villa — write to us and we'll respond within 24 hours.",
    name: "Name",
    email: "Email",
    phone: "Phone (optional)",
    message: "Message",
    submit: "Send enquiry",
    sending: "Sending…",
    success: "Thank you — we'll be in touch shortly.",
    error: "Unable to send. Please try again.",
  },
  booking: {
    overline: "Reservations",
    heading: "Reserve your dates.",
    sub: "Minimum stay {n} nights. Payment secured via Stripe. Dates shown in grey are unavailable.",
    select_stay: "Select your stay",
    checkin: "Check-in",
    checkout: "Check-out",
    nights: "Nights",
    guests: "Guests",
    guest_details: "Guest details",
    full_name: "Full name",
    email: "Email",
    phone: "Phone",
    notes: "Notes (optional)",
    summary: "Reservation summary",
    cleaning_fee: "Cleaning fee",
    deposit: "Refundable damage deposit",
    deposit_note: "The €390 damage deposit is refunded within 3 days of check-out, provided there are no damages.",
    total: "Total",
    select_prompt: "Select your dates to see the price.",
    pay: "Pay & Reserve",
    redirecting: "Redirecting…",
    secured: "Secured by Stripe · No booking fee",
    referral_title: "Referral code (optional)",
    referral_off: "off",
    referral_remove: "Remove",
    referral_placeholder: "IGV-XXXXXX",
    referral_apply: "Apply",
    referral_hint: "Friends who stayed here can share a code — €150 off your stay.",
    referral_line: "Referral",
  },
  success: {
    tag_ok: "Confirmed",
    tag_pending: "Processing",
    heading_ok: "Your reservation is confirmed.",
    heading_pending: "Confirming your payment…",
    heading_await: "Awaiting confirmation.",
    guest: "Guest",
    booking: "Booking",
    total_paid: "Total paid",
    body: "A receipt has been sent to your inbox from Stripe. We'll be in touch with arrival details closer to your stay.",
    back: "Back to villa",
  },
  cancel: {
    tag: "Cancelled",
    heading: "Your payment was cancelled.",
    body: "No charge was made. Your dates remain available — return to the booking page to try again.",
    retry: "Try again",
    home: "Home",
  },
  footer: {
    tagline: "A tranquil garden retreat above the harbour of Kioni. Available for the summer season by direct reservation only.",
    contact: "Contact",
    discover: "Discover",
    elsewhere: "Find us elsewhere",
    home: "Home",
    reserve: "Reserve",
    owner_login: "Owner Login",
    guest_portal: "Guest Portal",
    location_val: "Kioni, Ithaca",
    location_country: "Ionian Sea, Greece",
    rights: "All rights reserved.",
    subtitle: "Private Rentals · Ionian Sea",
    photo_credits: "Placeholder photography of Kioni via Wikimedia Commons (CC BY-SA)",
  },
};
